import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Empty, useTheme } from '@prio365/prio365-react-library';
import moment from 'moment';
import { TaskSummaryDto } from '../TaskHubModule';
import { ExtendedTaskDto } from '../api/taskHubApi';
import { useChartStyles } from './chartStyles';

interface AssigneeBarChartProps {
  summary: TaskSummaryDto;
  allTasks: ExtendedTaskDto[];
  onAssigneeClick: (assignee: string) => void;
  maxItems?: number;
  selectedAssignees?: string[];
}

export const AssigneeBarChart: React.FC<AssigneeBarChartProps> = ({
  summary,
  allTasks,
  onAssigneeClick,
  maxItems = 15,
  selectedAssignees = [],
}) => {
  const classes = useChartStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const getStatusBarColor = useCallback(
    (status: string): string => {
      switch (status) {
        case 'completed':
          return theme.colors.base.green.default;
        case 'overdue':
          return theme.colors.base.red.default;
        case 'today':
          return theme.colors.base.yellow.default;
        case 'onTrack':
          return theme.colors.base.blue.default;
        default:
          return theme.colors.application.typography.muted;
      }
    },
    [theme]
  );

  const getAssigneeStatusBreakdown = useCallback(
    (assigneeName: string) => {
      const assigneeTasks = allTasks.filter((task) => {
        if (assigneeName === 'Unassigned') {
          return task.assignees.length === 0;
        }
        return task.assignees.includes(assigneeName);
      });

      const completed = assigneeTasks.filter(
        (t) => t.status === 'completed'
      ).length;
      const overdue = assigneeTasks.filter(
        (t) => t.dueDate && new Date(t.dueDate) < new Date()
      ).length;
      const today = assigneeTasks.filter(
        (t) => t.dueDate && moment(t.dueDate).isSame(moment(), 'day')
      ).length;
      const onTrack = assigneeTasks.length - completed - overdue - today;

      return {
        completed,
        overdue,
        today,
        onTrack,
        total: assigneeTasks.length,
      };
    },
    [allTasks]
  );

  const assigneeData = useMemo(() => {
    return Object.entries(summary.tasksByAssignee)
      .sort(([, a], [, b]) => b - a)
      .slice(0, maxItems)
      .map(([assignee, totalTasks]) => {
        const breakdown = getAssigneeStatusBreakdown(assignee);
        const isSelected = selectedAssignees.includes(assignee);
        const bars = [
          {
            fraction: breakdown.completed,
            color: getStatusBarColor('completed'),
            title: t('taskHub:dashboard.charts.statusTypes.completed'),
            content: `${breakdown.completed} ${breakdown.completed === 1 ? t('taskHub:dashboard.charts.taskSingular') : t('taskHub:dashboard.charts.taskPlural')}`,
          },
          {
            fraction: breakdown.onTrack,
            color: getStatusBarColor('onTrack'),
            title: t('taskHub:dashboard.charts.statusTypes.onTrack'),
            content: `${breakdown.onTrack} ${breakdown.onTrack === 1 ? t('taskHub:dashboard.charts.taskSingular') : t('taskHub:dashboard.charts.taskPlural')}`,
          },
          {
            fraction: breakdown.today,
            color: getStatusBarColor('today'),
            title: t('taskHub:dashboard.charts.statusTypes.today'),
            content: `${breakdown.today} ${breakdown.today === 1 ? t('taskHub:dashboard.charts.taskSingular') : t('taskHub:dashboard.charts.taskPlural')}`,
          },
          {
            fraction: breakdown.overdue,
            color: getStatusBarColor('overdue'),
            title: t('taskHub:dashboard.charts.statusTypes.overdue'),
            content: `${breakdown.overdue} ${breakdown.overdue === 1 ? t('taskHub:dashboard.charts.taskSingular') : t('taskHub:dashboard.charts.taskPlural')}`,
          },
        ].filter((bar) => bar.fraction > 0);

        return {
          assignee,
          totalTasks,
          bars,
          breakdown,
          isSelected,
        };
      });
  }, [
    summary.tasksByAssignee,
    maxItems,
    getAssigneeStatusBreakdown,
    selectedAssignees,
    t,
    getStatusBarColor,
  ]);

  return (
    <div className={classes.scrollableChartCard}>
      <h3 className={classes.cardTitle}>
        {t('taskHub:dashboard.charts.tasksByAssignee')}
      </h3>
      <div className={classes.scrollableContainer}>
        {assigneeData.map(
          ({ assignee, totalTasks, bars, breakdown, isSelected }) => (
            <div
              key={assignee}
              className={classes.chartItem}
              onClick={() => onAssigneeClick(assignee)}
              style={{
                backgroundColor: isSelected
                  ? theme.colors.base.primary.default + '10'
                  : 'transparent',
                borderLeft: isSelected
                  ? `3px solid ${theme.colors.base.primary.default}`
                  : '3px solid transparent',
              }}
            >
              <div className={classes.chartLabel}>
                <span
                  title={assignee}
                  style={{
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected
                      ? theme.colors.base.primary.default
                      : theme.colors.application.typography.default,
                  }}
                >
                  {assignee === 'Unassigned'
                    ? t('taskHub:table.unassigned')
                    : assignee}
                  {isSelected && ' ✓'}
                </span>
                <span className={classes.taskCount}>
                  {totalTasks}{' '}
                  {totalTasks !== 1
                    ? t('taskHub:dashboard.charts.taskPlural')
                    : t('taskHub:dashboard.charts.taskSingular')}
                </span>
              </div>
              {bars.length > 0 ? (
                <div className={classes.barChartContainer}>
                  <BarChart bars={bars} />
                </div>
              ) : (
                <div className={classes.emptyBarPlaceholder}>
                  {t('taskHub:dashboard.charts.noTaskDataAvailable')}
                </div>
              )}
            </div>
          )
        )}
        {assigneeData.length === 0 && (
          <Empty
            customMessage={t(
              'taskHub:dashboard.charts.noAssigneeDataAvailable'
            )}
          />
        )}
      </div>
    </div>
  );
};
